/**
 * == Controller: InsightsController
 *
 * Manages filters for the analytics/insights dashboard. Persists user
 * selections to cookies, ensures sensible default date ranges, and triggers
 * Turbo Stream refreshes when inputs change.
 *
 * === Responsibilities:
 * - Keep project/date/search filters synchronised between form, cookies, and URL.
 * - Request updated filter parameters and table rows via `fetchAction`.
 * - Support tag removal, bulk clearing, and incremental parameter updates.
 */
import PlatformBaseController from "./platform_base_controller";
import {
    appendActiveOnly,
    resetActiveOnly,
    setActiveOnly,
    visibleActiveOnlyInput,
} from "./mixins/active_only_filter";

// Main controller for handling project/document list filtering
export default class extends PlatformBaseController {
    static targets = [
        "projectSelect", "logTableRow", "logTableMobRow",
        "projectId", "filterTag", "logStartDate", "logEndDate",
        "filterForm", "filterSubmit", "filterHideLink"
    ];

    static values = {
        projectId: { type: String,  default: undefined },
        logId:     { type: Number,  default: undefined },
        filterTag: { type: String,  default: undefined },
        initDates: { type: Boolean, default: true } // server can toggle
    };

    connect() {
        if (super.connect) super.connect();
        if (this.hasFilterSubmitTarget) this.filterSubmitTarget.disabled = false;
        this.skipDefaultDates = false;
        this.toggleCommentStatusDisabled(this.element);
        this.handleShowComments = this.handleShowComments?.bind(this) || this.onShowComments.bind(this);
        this.handleHideComments = this.handleHideComments?.bind(this) || this.onHideComments.bind(this);
        window.addEventListener("insights:show-comments", this.handleShowComments);
        window.addEventListener("insights:hide-comments", this.handleHideComments);
        this.syncHighlightedLog();

        if (this.logIdValue) {
            this.openLogComments(this.logIdValue);
        }

        // If server said "don't default dates" (e.g., after removing a single date tag),
        // just capture what's in the fields as "initial".
        if (this.initDatesValue === false) {
            const form = this.element.closest?.("form") || this.element;
            const s    = form?.querySelector('[name="logs_start_date"]')?.value || "";
            const e    = form?.querySelector('[name="logs_end_date"]')?.value || "";
            this.initialDates = { startYMD: s, endYMD: e };
            return;
        }

        // Otherwise, ensure defaults and remember them for this page load
        const { startYMD, endYMD } = this.ensureFilterDates();
        this.initialDates = { startYMD, endYMD };
    }

    disconnect() {
        window.removeEventListener("insights:show-comments", this.handleShowComments);
        window.removeEventListener("insights:hide-comments", this.handleHideComments);
        if (super.disconnect) super.disconnect();
    }

    setDates({ startYMD, endYMD }, form = null) {
        const scope = form || this.element.closest?.("form") || this.element;

        const startInput = scope.querySelector('[name="logs_start_date"]');
        const endInput   = scope.querySelector('[name="logs_end_date"]');

        if (startInput) startInput.value = startYMD || "";
        if (endInput)   endInput.value   = endYMD || "";

        if (this.hasLogStartDateTarget) this.logStartDateTarget.value = startYMD || "";
        if (this.hasLogEndDateTarget)   this.logEndDateTarget.value   = endYMD || "";

        if (startYMD) this.setPersistentFilter("logs_start_date", startYMD);
        if (endYMD)   this.setPersistentFilter("logs_end_date", endYMD);
    }

    ensureFilterDates(event = null, period = 30) {
        const form =
            event?.target?.closest?.("form") ||
            event?.target?.form ||
            this.element.closest?.("form") ||
            this.element;

        const today        = this.startOfLocalDay(new Date());
        const defaultEnd   = this.formatYMD(today);                   // today
        const defaultStart = this.formatYMD(this.addDays(today, -period)); // period days before

        const startInput = form.querySelector('[name="logs_start_date"]');
        const endInput   = form.querySelector('[name="logs_end_date"]');

        // Only set if the field has no value (so we don't override server-rendered empties)
        if (startInput && !startInput.value) {
            startInput.value = defaultStart;
            if (this.hasLogStartDateTarget) this.logStartDateTarget.value = defaultStart;
            this.setPersistentFilter("logs_start_date", defaultStart);
        }
        if (endInput && !endInput.value) {
            endInput.value = defaultEnd;
            if (this.hasLogEndDateTarget) this.logEndDateTarget.value = defaultEnd;
            this.setPersistentFilter("logs_end_date", defaultEnd);
        }

        return {
            startYMD: startInput?.value || "",
            endYMD:   endInput?.value   || "",
            start:    startInput?.value ? this.toLocalDateOnly(startInput.value) : undefined,
            end:      endInput?.value   ? this.toLocalDateOnly(endInput.value)   : undefined
        };
    }

    // Handles project select dropdown changes
    selectProject(event) {
        const projectId = event.target.value;
        const activeOnlyInput = visibleActiveOnlyInput();
        const params = new URLSearchParams();
        appendActiveOnly(params, { root: activeOnlyInput?.closest("form") || document });
        const withComments = this.withCommentsChecked(activeOnlyInput?.closest("form") || document);
        params.set("with_comments", withComments ? "1" : "0");
        const newActivities = this.newActivitiesChecked(activeOnlyInput?.closest("form") || document);
        params.set("new_activities", newActivities ? "1" : "0");
        const commentStatus = newActivities ? "" : this.commentStatusValue(activeOnlyInput?.closest("form") || document);
        if (commentStatus) params.set("comments_status", commentStatus);
        this.setPersistentFilter("project_id", projectId);
        this.updateProjectIdInputs(projectId);
        params.set("project_id", projectId);
        this.fetchAction(`/insights/filter_parameters?${params.toString()}`);
    }

    updateProjectIdInputs(projectId) {
        const targets = [];
        if (this.hasFilterFormTarget) this.filterFormTargets.forEach((form) => targets.push(form));
        if (this.element?.closest) targets.push(this.element.closest("form"));
        targets.push(this.element);

        targets.forEach((form) => {
            if (!form || typeof form.querySelector !== "function") return;
            const hidden = form.querySelector('[name="project_id"]');
            if (hidden) hidden.value = projectId;
        });
    }

    removeFilterTag(event) {
        event.preventDefault();

        const activeOnlyInput = visibleActiveOnlyInput();

        const clickedElement = event.currentTarget;
        const filterTag      = clickedElement.dataset.filterTagValue;
        const form           = clickedElement.closest("form") || this.element.closest?.("form") || this.element;
        const root           = activeOnlyInput?.closest("form") || form || document;

        if (filterTag) {
            this.deletePersistentFilter(filterTag);
            this.removeParam(filterTag);
        }

        // Reflect emptiness immediately in the mirrored inputs
        if (filterTag === "logs_start_date") {
            if (this.hasLogStartDateTarget) this.logStartDateTarget.value = "";
            const input = root?.querySelector?.('[name="logs_start_date"]');
            if (input) input.value = "";
            this.skipDefaultDates = true;
        }
        if (filterTag === "logs_end_date") {
            if (this.hasLogEndDateTarget) this.logEndDateTarget.value = "";
            const input = root?.querySelector?.('[name="logs_end_date"]');
            if (input) input.value = "";
            this.skipDefaultDates = true;
        }
        if (filterTag === "active_only") {
            setActiveOnly(false);
        }
        if (filterTag === "with_comments") {
            this.setWithComments(false, root);
        }
        if (filterTag === "comments_status") {
            this.setCommentStatus("", root);
        }
        if (filterTag === "new_activities") {
            this.setNewActivities(false, root);
            this.toggleCommentStatusDisabled(root);
        }
        clickedElement.remove();
    }

    clearFilters(event) {
        event.preventDefault();

        const form      = event.target.closest("form") || event.target.form || this.element;
        const projectId = event.currentTarget?.dataset?.projectIdValue;

        if (projectId !== undefined) this.setPersistentFilter("project_id", projectId);

        document.querySelectorAll('input[name="search"]').forEach((input) => {
            input.value = "";
        });

        ["logs_start_date", "logs_end_date", "search"].forEach((key) => this.removeParam(key));
        this.deletePersistentFilter("logs_start_date");
        this.deletePersistentFilter("logs_end_date");

        const today = this.startOfLocalDay(new Date());
        const defaultEnd = this.formatYMD(today);
        const defaultStart = this.formatYMD(this.addDays(today, -30));
        const defaultDates = { startYMD: defaultStart, endYMD: defaultEnd };
        this.setDates(defaultDates, form);
        this.initialDates = defaultDates;
        this.skipDefaultDates = false;

        const params = new URLSearchParams();
        if (projectId) params.set("project_id", projectId);
        resetActiveOnly();
        this.setWithComments(false);
        this.setCommentStatus("", form);
        this.setNewActivities(false, form);
        this.toggleCommentStatusDisabled(form);
        appendActiveOnly(params, { root: form });
        params.set("with_comments", "0");
        params.set("new_activities", "0");

        const query = params.toString();
        const url   = query ? `/insights/clear_filters?${query}` : "/insights/clear_filters";
        this.fetchAction(url);
    }

    setFilters(event) {
        event.preventDefault();
        if (this.hasFilterSubmitTarget) this.filterSubmitTarget.disabled = false;

        const form      = event.target.closest("form") || event.target.form || this.element;
        const projectId = this.getPersistedFilter(form, "project_id");
        const search    = this.getSearch(form);
        const s         = this.getPersistedFilter(form, "logs_start_date");
        const e         = this.getPersistedFilter(form, "logs_end_date");

        if (projectId !== undefined) this.setPersistentFilter("project_id", projectId);

        if (s === undefined && this.hasLogStartDateTarget) this.logStartDateTarget.value = "";
        if (e === undefined && this.hasLogEndDateTarget)   this.logEndDateTarget.value   = "";
        if (!this.skipDefaultDates) this.ensureFilterDates();

        const params = new URLSearchParams();
        if (search)   params.append("search", search);
        if (projectId) params.append("project_id", projectId);
        params.append("logs_start_date", this.getPersistedFilter(form, "logs_start_date"));
        params.append("logs_end_date",   this.getPersistedFilter(form, "logs_end_date"));
        appendActiveOnly(params, { root: form });
        params.set("with_comments", this.withCommentsChecked(form) ? "1" : "0");
        const newActivities = this.newActivitiesChecked(form);
        params.set("new_activities", newActivities ? "1" : "0");
        const commentStatus = newActivities ? "" : this.commentStatusValue(form);
        if (commentStatus) params.set("comments_status", commentStatus);

        this.fetchAction(`/insights/filter_parameters?${params.toString()}`);
    }

    // Handles full filter form submissions
    filterParameters(event) {
        event.preventDefault();

        const form      = event.target.closest("form") || event.target.form || this.element;
        const projectId = this.getPersistedFilter(form, "project_id");
        const search    = this.getSearch(form);
        const s         = this.getPersistedFilter(form, "logs_start_date");
        const e         = this.getPersistedFilter(form, "logs_end_date");

        if (projectId !== undefined) this.setPersistentFilter("project_id", projectId);

        if (s === undefined && this.hasLogStartDateTarget) this.logStartDateTarget.value = "";
        if (e === undefined && this.hasLogEndDateTarget)   this.logEndDateTarget.value   = "";

        const params = new URLSearchParams();
        if (search)   params.append("search", search);
        if (projectId) params.append("project_id", projectId);
        if (s) params.append("logs_start_date", s);
        if (e) params.append("logs_end_date",   e);
        appendActiveOnly(params, { root: form });
        params.set("with_comments", this.withCommentsChecked(form) ? "1" : "0");
        const newActivities = this.newActivitiesChecked(form);
        params.set("new_activities", newActivities ? "1" : "0");
        const commentStatus = newActivities ? "" : this.commentStatusValue(form);
        if (commentStatus) params.set("comments_status", commentStatus);

        this.fetchAction(`/insights/filter_parameters?${params.toString()}`);
    }

    updateFilterDate(event) {
        const input = event.target;
        const { name, value } = input;
        if (name === "logs_start_date" || name === "logs_end_date") {
            this.setPersistentFilter(name, value);
            if (name === "logs_start_date" && this.hasLogStartDateTarget) this.logStartDateTarget.value = value;
            if (name === "logs_end_date"   && this.hasLogEndDateTarget)   this.logEndDateTarget.value   = value;
        }
    }

    showComments(event) {
        event.preventDefault();
        const logId = event.currentTarget?.dataset?.logId;
        if (!logId) return;
        document.querySelector('.workspaces_block.active') && window.innerWidth > 1400 && document.querySelector('.workspaces_hide')?.click();
        this.openLogComments(logId);
    }

    openLogComments(logId) {
        if (!logId) return;
        this.highlightLog(logId);
        const params = new URLSearchParams({ log_id: logId, panel_state: "expanded" });
        this.fetchAction(`/insights/show_comments?${params.toString()}`, {
            onSuccess: () => window.dispatchEvent(new CustomEvent("insights:show-comments", { detail: { logId, source: "log-selection" } }))
        });
    }

    withCommentsChecked(root = document) {
        const input = root?.querySelector?.('input[name="with_comments"]');
        return Boolean(input?.checked);
    }

    setWithComments(checked, root = document) {
        root.querySelectorAll?.('input[name="with_comments"]').forEach((input) => {
            input.checked = checked;
        });
    }

    commentStatusValue(root = document) {
        const input = root?.querySelector?.('select[name="comments_status"]');
        return input?.value || "";
    }

    setCommentStatus(value, root = document) {
        root.querySelectorAll?.('select[name="comments_status"]').forEach((input) => {
            input.value = value;
        });
    }

    newActivitiesChecked(root = document) {
        const input = root?.querySelector?.('input[name="new_activities"]');
        return Boolean(input?.checked);
    }

    setNewActivities(checked, root = document) {
        root.querySelectorAll?.('input[name="new_activities"]').forEach((input) => {
            input.checked = checked;
        });
        this.toggleCommentStatusDisabled(root);
    }

    toggleCommentStatusDisabled(root = document) {
        const disabled = this.newActivitiesChecked(root);
        root.querySelectorAll?.('select[name="comments_status"]').forEach((input) => {
            input.disabled = disabled;
            if (disabled) input.value = "";
        });
    }

    onShowComments(event) {
        const logId = event?.detail?.logId;
        if (!logId) return;
        this.highlightLog(logId);
    }

    onHideComments() {
        this.clearHighlightedLogs();
    }

    syncHighlightedLog() {
        const drawer = document.querySelector("#log_comments[data-comments-state='expanded']");
        const logId = drawer?.querySelector?.('input[name="log_id"]')?.value;
        if (logId) this.highlightLog(logId);
    }

    highlightLog(logId) {
        this.clearHighlightedLogs();
        this.element.querySelectorAll(`[data-log-id-value="${logId}"]`).forEach((row) => {
            row.classList.add("logs_table_row--comments-active");
        });
    }

    clearHighlightedLogs() {
        this.element.querySelectorAll(".logs_table_row--comments-active").forEach((row) => {
            row.classList.remove("logs_table_row--comments-active");
        });
    }
}
